import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchTours } from "../lib/api";
import TourCard from "../components/TourCard";
import SEO from "../components/SEO";
import { Loader2, SlidersHorizontal, X } from "lucide-react";
import { useSettings } from "../lib/settings";

const FALLBACK_CATEGORIES = [
  { key: "small-group", label: "Small group" },
  { key: "private", label: "Private" },
  { key: "cooking", label: "Cooking classes" },
  { key: "workshops", label: "Workshops" },
  { key: "day-trips", label: "Day trips" },
];

const GROUP_TYPE_OPTIONS = [
  { value: "all", label: "Group or private" },
  { value: "group", label: "Group tour" },
  { value: "private", label: "Private tour" },
];

const DURATION_OPTIONS = [
  { value: "all", label: "Any duration" },
  { value: "short", label: "Under 3 hours" },
  { value: "medium", label: "3–5 hours" },
  { value: "long", label: "5–8 hours" },
  { value: "xlong", label: "8+ hours" },
];

const PRICE_OPTIONS = [
  { value: "all", label: "Any price" },
  { value: "p1", label: "Under €75" },
  { value: "p2", label: "€75 – €150" },
  { value: "p3", label: "€150 – €500" },
  { value: "p4", label: "€500+" },
];

const TIME_OPTIONS = [
  { value: "all", label: "Any time of day" },
  { value: "morning", label: "Morning" },
  { value: "afternoon", label: "Afternoon" },
  { value: "evening", label: "Evening" },
  { value: "full-day", label: "Full day" },
  { value: "flexible", label: "Flexible" },
];

const SORT_OPTIONS = [
  { value: "popular", label: "Most popular" },
  { value: "rating", label: "Highest rated" },
  { value: "price-asc", label: "Price: low to high" },
  { value: "price-desc", label: "Price: high to low" },
];

const TYPE_TAGS = [
  { value: "walking", label: "Walking tours" },
  { value: "food", label: "Food tours" },
  { value: "boat", label: "Boat tours" },
  { value: "cultural", label: "Cultural tours" },
  { value: "experience", label: "Experiences" },
];

const parseDurationHours = (duration) => {
  const match = String(duration || "").match(/[\d.]+/);
  return match ? parseFloat(match[0]) : null;
};

const AllToursPage = () => {
  const { settings } = useSettings();
  const CATEGORY_OPTIONS = useMemo(() => {
    const cats = (settings?.tour_categories && settings.tour_categories.length > 0)
      ? settings.tour_categories.map((c) => ({ value: c.key, label: c.label || c.key }))
      : FALLBACK_CATEGORIES.map((c) => ({ value: c.key, label: c.label }));
    return [{ value: "all", label: "All categories" }, ...cats];
  }, [settings]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [category, setCategory] = useState("all");
  const [groupType, setGroupType] = useState("all");
  const [duration, setDuration] = useState("all");
  const [price, setPrice] = useState("all");
  const [timeOfDay, setTimeOfDay] = useState("all");
  const [activeTags, setActiveTags] = useState([]);
  const [sort, setSort] = useState("popular");

  useEffect(() => {
    setLoading(true);
    fetchTours()
      .then((data) => setTours(data))
      .catch(() => setTours([]))
      .finally(() => setLoading(false));
  }, []);

  const toggleTag = (tag) => {
    setActiveTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const resetFilters = () => {
    setCategory("all");
    setGroupType("all");
    setDuration("all");
    setPrice("all");
    setTimeOfDay("all");
    setActiveTags([]);
    setSort("popular");
  };

  const filtered = useMemo(() => {
    let list = [...tours];

    if (category !== "all") list = list.filter((t) => t.category === category);

    if (groupType !== "all") {
      list = list.filter((t) => (groupType === "private" ? t.category === "private" : t.category !== "private"));
    }

    if (duration !== "all") {
      list = list.filter((t) => {
        const hrs = parseDurationHours(t.duration);
        if (hrs == null) return false;
        if (duration === "short") return hrs < 3;
        if (duration === "medium") return hrs >= 3 && hrs <= 5;
        if (duration === "long") return hrs > 5 && hrs <= 8;
        if (duration === "xlong") return hrs > 8;
        return true;
      });
    }

    if (price !== "all") {
      list = list.filter((t) => {
        const p = t.price_number;
        if (p == null) return false;
        if (price === "p1") return p < 75;
        if (price === "p2") return p >= 75 && p <= 150;
        if (price === "p3") return p > 150 && p <= 500;
        if (price === "p4") return p > 500;
        return true;
      });
    }

    if (timeOfDay !== "all") list = list.filter((t) => t.time_of_day === timeOfDay);

    if (activeTags.length > 0) {
      list = list.filter((t) => activeTags.every((tag) => (t.tags || []).includes(tag)));
    }

    list.sort((a, b) => {
      if (sort === "rating") return (b.rating || 0) - (a.rating || 0);
      if (sort === "price-asc") return (a.price_number ?? Infinity) - (b.price_number ?? Infinity);
      if (sort === "price-desc") return (b.price_number ?? -Infinity) - (a.price_number ?? -Infinity);
      return (b.popularity || 0) - (a.popularity || 0);
    });

    return list;
  }, [tours, category, groupType, duration, price, timeOfDay, activeTags, sort]);

  const activeFilterCount =
    (category !== "all" ? 1 : 0) +
    (groupType !== "all" ? 1 : 0) +
    (duration !== "all" ? 1 : 0) +
    (price !== "all" ? 1 : 0) +
    (timeOfDay !== "all" ? 1 : 0) +
    activeTags.length;

  const selectClass =
    "h-11 rounded-full border border-[var(--ri-border)] bg-[#fffdf7] px-4 text-[13.5px] text-[var(--ri-ink)] focus:outline-none focus:ring-1 focus:ring-[var(--ri-red)] cursor-pointer";

  const toursLd = filtered.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: filtered.slice(0, 20).map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${window.location.origin}/tour/${t.slug || t.id}`,
      name: t.title,
    })),
  } : null;

  return (
    <div>
      <SEO
        title="All Tours · Istanbul Free Walking Tours"
        description="Browse and filter every Istanbul tour we offer — walking tours, food tours, boat tours, cultural tours, private experiences and more."
        path="/tours"
        jsonLd={toursLd || undefined}
      />

      <section className="relative min-h-[320px] text-white overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1524231757912-21f4fe3a7200"
          alt="Istanbul tours"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative container-x min-h-[320px] flex flex-col justify-end pb-12">
          <p className="eyebrow text-[#ffb9a8] mb-3">Every tour, one place</p>
          <h1 className="font-serif-display text-[36px] md:text-[52px] leading-[1.05] max-w-[820px]">
            Find your Istanbul tour.
          </h1>
          <p className="mt-3 max-w-[600px] text-[15px] text-[#f6ecdf]">
            Filter by category, duration, price and more to find the tour that fits your trip.
          </p>
        </div>
      </section>

      <section className="section-tight bg-[var(--ri-cream)]">
        <div className="container-x">
          {/* Mobile filter toggle */}
          <div className="lg:hidden mb-4">
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="btn-outline w-full flex items-center justify-center gap-2"
            >
              <SlidersHorizontal size={16} />
              {filtersOpen ? "Hide filters" : "Filters & sort"}
              {activeFilterCount > 0 && (
                <span className="ml-1 bg-[var(--ri-red)] text-white text-[11px] font-semibold rounded-full w-5 h-5 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          <div className={`${filtersOpen ? "block" : "hidden"} lg:block`}>
            <div className="flex flex-wrap items-center gap-2.5 pb-4 mb-2">
              <select className={selectClass} value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <select className={selectClass} value={groupType} onChange={(e) => setGroupType(e.target.value)}>
                {GROUP_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <select className={selectClass} value={duration} onChange={(e) => setDuration(e.target.value)}>
                {DURATION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <select className={selectClass} value={price} onChange={(e) => setPrice(e.target.value)}>
                {PRICE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <select className={selectClass} value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)}>
                {TIME_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>

              <div className="w-px h-6 bg-[var(--ri-border)] mx-1 hidden sm:block" />

              <select
                className={`${selectClass} font-semibold`}
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>Sort: {o.label}</option>
                ))}
              </select>

              {activeFilterCount > 0 && (
                <button
                  onClick={resetFilters}
                  className="flex items-center gap-1 text-[13px] text-[var(--ri-muted)] hover:text-[var(--ri-red)] transition-colors px-2"
                >
                  <X size={14} /> Clear filters
                </button>
              )}
            </div>

            <div className="flex flex-wrap gap-2 pb-6">
              {TYPE_TAGS.map((tag) => {
                const active = activeTags.includes(tag.value);
                return (
                  <button
                    key={tag.value}
                    onClick={() => toggleTag(tag.value)}
                    className="text-[12.5px] font-medium px-3.5 py-1.5 rounded-full border transition-colors"
                    style={
                      active
                        ? { background: "var(--ri-red)", color: "#fff", borderColor: "var(--ri-red)" }
                        : { background: "#fffdf7", color: "var(--ri-ink-soft)", borderColor: "var(--ri-border)" }
                    }
                  >
                    {tag.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="divider mb-8" />

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="animate-spin text-[var(--ri-red)]" size={32} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <h3 className="font-serif-display text-[26px]">No tours match those filters.</h3>
              <p className="mt-3 text-[var(--ri-muted)]">Try widening your search, or contact us for a custom option.</p>
              <div className="mt-6 flex items-center justify-center gap-3">
                <button onClick={resetFilters} className="btn-outline">Clear filters</button>
                <Link to="/contact" className="btn-primary">Contact us</Link>
              </div>
            </div>
          ) : (
            <>
              <p className="text-[13.5px] text-[var(--ri-muted)] mb-5">
                {filtered.length} tour{filtered.length !== 1 ? "s" : ""} found
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((t) => (
                  <TourCard key={t.id} tour={t} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default AllToursPage;
